import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { funnel, lossReasons, treatments } from '../data/mockData';

const { FiArrowUp, FiInfo, FiArrowLeft, FiSparkles, FiAlertCircle } = FiIcons;

const formatMoney = (value) => `${value.toLocaleString('en-US')} ر.س`;

export default function Dashboard({ navigate, language }) {
  const english = language === 'en';
  const [sort, setSort] = useState('value');
  const sortedTreatments = [...treatments].sort((a, b) => sort === 'name' ? a.name.localeCompare(b.name) : b[sort] - a[sort]);

  return (
    <div className="dashboard-page">
      <header className="page-header">
        <div><h1>{english ? 'Overview' : 'نظرة عامة'}</h1><p>{english ? 'Riyadh Smile Clinics' : 'عيادات ابتسامة الرياض'}</p></div>
        <select defaultValue="aug"><option value="aug">{english ? 'August 2026' : 'أغسطس 2026'}</option><option>{english ? 'July 2026' : 'يوليو 2026'}</option></select>
      </header>

      <section className="hero-metric">
        <div className="metric-label">
          <span>{english ? 'Estimated Lost Revenue' : 'الإيرادات المقدّرة المفقودة'}</span>
          <span className="tooltip"><SafeIcon icon={FiInfo} /><i>{english ? 'An estimate based on service values and lost opportunities, not confirmed accounting revenue.' : 'هذا تقدير يعتمد على قيمة الخدمات والفرص المفقودة، وليس إيرادًا محاسبيًا مؤكدًا.'}</i></span>
        </div>
        <strong>68,450 <small>ر.س</small></strong>
        <p><SafeIcon icon={FiArrowUp} /> {english ? '17% compared with the previous period' : '17% مقارنة بالفترة السابقة'}</p>
      </section>

      <section className="kpi-grid">
        <Kpi label={english ? 'Lost Opportunities' : 'الفرص المفقودة'} value="57" />
        <Kpi label={english ? 'Recoverable Opportunities' : 'فرص قابلة للاستعادة'} value="17" accent />
        <Kpi label={english ? 'Potential Recoverable Revenue' : 'إيرادات محتملة قابلة للاستعادة'} value="21,200 ر.س" accent />
      </section>

      <section className="dashboard-grid">
        <LossReasons english={english} />
        <InsightCard navigate={navigate} english={english} />
      </section>

      <Funnel english={english} />
      <Treatments data={sortedTreatments} sort={sort} setSort={setSort} english={english} />
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return <article className={`kpi-card ${accent ? 'accent' : ''}`}><span>{label}</span><strong>{value}</strong></article>;
}

export function LossReasons({ english = false }) {
  return (
    <article className="panel loss-panel">
      <div className="panel-heading"><div><h2>{english ? 'Why are we losing leads?' : 'لماذا نخسر العملاء؟'}</h2><p>{english ? 'Share of lost opportunities by primary reason' : 'نسبة الفرص المفقودة حسب السبب الرئيسي'}</p></div></div>
      <div className="bar-list">
        {lossReasons.map((reason, index) => (
          <div className={index === 0 ? 'highlight' : ''} key={reason.label}>
            <span><b>{english ? reason.en : reason.label}</b><em>{reason.value}%</em></span>
            <div><i style={{ width: `${reason.value * 2.8}%` }} /></div>
          </div>
        ))}
      </div>
    </article>
  );
}

function InsightCard({ navigate, english }) {
  return (
    <article className="insight-card">
      <span className="insight-icon"><SafeIcon icon={FiSparkles} /></span>
      <span className="analysis-label">{english ? 'Cencey Analysis' : 'تحليل Cencey'}</span>
      <h2>{english ? 'Your biggest problem is not price' : 'أكبر مشكلة لديك ليست السعر'}</h2>
      <p>{english ? '31% of lost opportunities did not receive a second follow-up.' : '31% من الفرص المفقودة لم تحصل على متابعة ثانية.'}</p>
      <div className="insight-value"><small>{english ? 'Estimated revenue exposed due to this issue' : 'الإيرادات المقدّرة المعرضة للخسارة بسبب هذه المشكلة'}</small><strong>21,200 ر.س</strong></div>
      <div className="recommendation"><b>{english ? 'Recommendation' : 'التوصية'}</b><p>{english ? 'Add a second follow-up within 48 hours for qualified leads that did not book.' : 'أضف متابعة ثانية خلال 48 ساعة للعملاء المؤهلين الذين لم يحجزوا.'}</p></div>
      <button className="btn light-btn" onClick={() => navigate('/recoverable')}>{english ? 'View recovery opportunities' : 'عرض فرص الاستعادة'} <SafeIcon icon={FiArrowLeft} /></button>
    </article>
  );
}

export function Funnel({ english = false }) {
  return (
    <article className="panel funnel-panel">
      <div className="panel-heading"><div><h2>{english ? 'Lead conversion journey' : 'رحلة تحويل العملاء'}</h2><p>{english ? 'From first inquiry to treatment acceptance' : 'من الاستفسار الأول حتى الموافقة على العلاج'}</p></div></div>
      <div className="funnel">
        {funnel.map((stage, index) => {
          const drop = index ? Math.round((1 - stage.count / funnel[index - 1].count) * 100) : null;
          return (
            <React.Fragment key={stage.label}>
              {drop && <span className={drop > 40 ? 'drop high' : 'drop'}>−{drop}%</span>}
              <div><span>{english ? stage.en : stage.label}</span><strong>{stage.count}</strong></div>
            </React.Fragment>
          );
        })}
      </div>
      <p className="funnel-insight"><SafeIcon icon={FiAlertCircle} /> {english ? 'The largest current leakage occurs between attending the appointment and accepting treatment.' : 'أكبر تسرب حاليًا يحدث بين حضور الموعد والموافقة على العلاج.'}</p>
    </article>
  );
}

function Treatments({ data, sort, setSort, english }) {
  return (
    <article className="panel treatments-panel">
      <div className="panel-heading">
        <div><h2>{english ? 'Leakage by service' : 'التسرب حسب الخدمة'}</h2><p>{english ? 'Services with the greatest estimated financial impact' : 'الخدمات ذات التأثير المالي المقدّر الأكبر'}</p></div>
        <select value={sort} onChange={(event) => setSort(event.target.value)}>
          <option value="value">{english ? 'Estimated revenue' : 'الإيرادات المقدّرة'}</option>
          <option value="lost">{english ? 'Opportunity count' : 'عدد الفرص'}</option>
          <option value="name">{english ? 'Service' : 'الخدمة'}</option>
        </select>
      </div>
      <div className="treatment-list">
        {data.map((item) => <div key={item.name}><span><b>{english ? item.en : item.name}</b><small>{item.lost} {english ? 'lost opportunities' : 'فرصة مفقودة'}</small></span><strong>{formatMoney(item.value)}</strong></div>)}
      </div>
    </article>
  );
}